import { useQuery } from '@tanstack/react-query';
import api from '@/services/api';
import { HittingBayAvailabilityEntry } from '@/types/booking.types';
import { SHORT_CACHE_TIME_MS } from '@/constants/timings';

// Query key factory for hitting bay availability
export const hittingBayAvailabilityKeys = {
  all: ['hitting-bay-availability'] as const,
  forLocation: (locationId: string, startDate: string, rescheduleBookingId?: string) =>
    [
      ...hittingBayAvailabilityKeys.all,
      locationId,
      startDate,
      rescheduleBookingId ?? '',
    ] as const,
};

/**
 * Hook to get aggregated hitting bay availability for a location and start date.
 * Fetches the member-facing GET /bookings/hitting-bay/availability endpoint (JwtAuthGuard).
 * Query is disabled when either locationId or startDate is empty.
 */
export const useHittingBayAvailability = (
  locationId: string,
  startDate: string,
  /**
   * The booking being moved. Sent so the API stops reporting that booking's
   * own bay and times as busy — without it a member rescheduling 6-8 on Bay 4
   * to 7-9 is told 7 is unavailable, by themselves (ENG-595).
   */
  rescheduleBookingId?: string,
) => {
  const query = useQuery<HittingBayAvailabilityEntry[], Error>({
    queryKey: hittingBayAvailabilityKeys.forLocation(
      locationId,
      startDate,
      rescheduleBookingId,
    ),
    queryFn: async () => {
      const response = await api.get('/bookings/hitting-bay/availability', {
        params: {
          locationId,
          startDate,
          ...(rescheduleBookingId ? { rescheduleBookingId } : {}),
        },
      });
      return response.data;
    },
    enabled: Boolean(locationId && startDate),
    staleTime: SHORT_CACHE_TIME_MS,
    refetchOnMount: 'always', // always fetch fresh on page entry; date/bay changes use the cache
    refetchOnWindowFocus: false,
    retry: false,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
};
